import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Inject, Injectable, Injector, inject } from '@angular/core';
import { MsalService, MSAL_INTERCEPTOR_CONFIG, MsalInterceptorConfiguration } from '@azure/msal-angular';
import { AccountInfo } from '@azure/msal-browser';
import { Observable, from } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AppStateHolder } from './app-state.holder';
import { AuthSessionService } from '../services/auth-session.service';
import { isInteractionRequiredError } from './msal-error.utils';

/**
 * Replacement di `MsalInterceptor` SENZA fallback interattivo (popup/redirect).
 *
 * Comportamento:
 *  - Match URL contro `protectedResourceMap` (prefix-based) per ottenere gli scope.
 *  - Se URL non in mappa -> request passa intatta (es. assets/i18n).
 *  - Se URL in mappa: tenta `acquireTokenSilent`. Se accessToken="" (bug B2C noto),
 *    ritenta con `forceRefresh: true` (stesso pattern del pre-warm in main.ts).
 *    - Successo con accessToken non vuoto -> inietta `Authorization: Bearer ...`.
 *    - Anche il forceRefresh torna vuoto / silent fail -> request parte SENZA bearer.
 *      Backend risponderà 401 e `AuthExpiredInterceptor` gestirà silent refresh
 *      o session expired (redirect controllato a /login).
 *
 * Differenza rispetto a `MsalInterceptor` v3.1 di @azure/msal-angular: niente
 * `acquireTokenPopup` né `acquireTokenRedirect` come fallback. Niente modali B2C
 * inattese durante l'uso dell'app — il redirect a B2C avviene SOLO via il flusso
 * controllato di `AuthSessionService.handleSessionExpired`.
 */
@Injectable()
export class MsalSilentInterceptor implements HttpInterceptor {
  private msalService = inject(MsalService);
  private appStateHolder = inject(AppStateHolder);
  // Lazy injection per evitare cicli con AuthSessionService (che a sua volta
  // potrebbe transitively usare servizi che dipendono da HttpClient).
  private injector = inject(Injector);

  constructor(
    @Inject(MSAL_INTERCEPTOR_CONFIG) private readonly interceptorConfig: MsalInterceptorConfiguration,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const scopes = this.getScopesForUrl(req.url);
    if (!scopes || scopes.length === 0) {
      return next.handle(req);
    }

    const account =
      this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      // Nessun account loggato: passa senza Bearer.
      // AuthExpiredInterceptor / route guard gestiranno il redirect a login se serve.
      return next.handle(req);
    }

    return from(this.acquireTokenWithB2CFallback(scopes, account, req.url)).pipe(
      switchMap(token => {
        if (!token) {
          return next.handle(req);
        }
        let headers = req.headers.set('Authorization', `Bearer ${token}`);
        const impersonationId = this.resolveImpersonationId(req.url);
        if (impersonationId) {
          headers = headers.set('X-Impersonation-Id', impersonationId);
        }
        return next.handle(req.clone({ headers }));
      }),
      catchError(() => next.handle(req)),
    );
  }

  /**
   * Workaround per bug noto B2C: il primo `acquireTokenSilent` post-redirect
   * o dopo idle puo' ritornare AuthenticationResult con accessToken="".
   * Replica il pattern del pre-warm in main.ts: silent -> se vuoto, silent
   * con forceRefresh. Senza questo fallback la request parte senza Bearer
   * e il backend (es. AuthN) risponde 401.
   *
   * Se silent fallisce con un errore che richiede interazione (refresh token
   * scaduto -> AADB2C90085 invalid_grant, silent iframe bloccato da
   * X-Frame-Options: deny di B2C), triggera reauthenticate fire-and-forget.
   * Senza questo trigger, le richieste verso endpoint che autorizzano via
   * cookie .AspNet.Cookies (legacy) ritornano 200 e l'app resta in stato
   * zombie: MSAL morto, niente Bearer, nessun 401 -> nessun redirect a /login
   * mai. Mutex + cooldown interni a AuthSessionService evitano reauth spam.
   */
  private async acquireTokenWithB2CFallback(scopes: string[], account: AccountInfo, url: string): Promise<string | null> {
    try {
      let result = await this.msalService.instance.acquireTokenSilent({ scopes, account });
      if (!result?.accessToken) {
        result = await this.msalService.instance.acquireTokenSilent({ scopes, account, forceRefresh: true });
      }
      return result?.accessToken || null;
    } catch (error: unknown) {
      if (isInteractionRequiredError(error)) {
        const authSession = this.injector.get(AuthSessionService);
        authSession.reauthenticate(`MsalSilentInterceptor InteractionRequired (${url})`);
      }
      return null;
    }
  }

  /**
   * Match URL contro le chiavi assolute di `protectedResourceMap` usando origin
   * identica e boundary di path. Evita leak di Bearer verso host lookalike.
   */
  private getScopesForUrl(url: string): string[] | null {
    const map = this.interceptorConfig.protectedResourceMap;
    if (!map) return null;

    for (const [key, value] of map.entries()) {
      if (typeof key === 'string' && this.isUrlWithinBase(url, key)) {
        return Array.isArray(value) ? value as string[] : null;
      }
    }
    return null;
  }

  private resolveImpersonationId(url: string): string | null {
    if (!this.isUrlWithinBase(url, environment.baseUrlAuthN)) {
      return null;
    }

    const impersonationId = this.appStateHolder.getCookie('ImpersonationSessionId');
    return this.isNonEmptyString(impersonationId) ? impersonationId.trim() : null;
  }

  private isUrlWithinBase(url: string, baseUrl: string): boolean {
    try {
      const requestUrl = new URL(url);
      const base = new URL(baseUrl);
      const basePath = base.pathname.endsWith('/') ? base.pathname.slice(0, -1) : base.pathname;
      const requestPath = requestUrl.pathname;

      return requestUrl.origin === base.origin
        && (basePath === '' || requestPath === basePath || requestPath.startsWith(`${basePath}/`));
    } catch {
      return false;
    }
  }

  private isNonEmptyString(value: string | null | undefined): value is string {
    return !!value && value.trim().length > 0;
  }
}
